/**
 * analytics.js
 *
 * Central GA4 wrapper. All tracking calls go through here so that:
 *  - GA4 is never initialized without explicit cookie consent.
 *  - Event names are consistent across the codebase.
 *  - Easy to swap / disable analytics in one place.
 */

import ReactGA from 'react-ga4';

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

let _initialized = false;

// ── Init ──────────────────────────────────────────────────────────────────────

/**
 * Call once after the user grants analytics consent.
 * Safe to call multiple times — only initializes once.
 */
export const initGA = () => {
  if (_initialized || !MEASUREMENT_ID || MEASUREMENT_ID === 'G-XXXXXXXXXX') return;
  ReactGA.initialize(MEASUREMENT_ID, {
    gaOptions: {
      // Anonymize IP addresses before sending to GA4 (GDPR best-practice)
      anonymize_ip: true,
    },
    gtagOptions: {
      // Disable advertising personalization signals
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    },
  });
  _initialized = true;
};

export const isInitialized = () => _initialized;

// ── Core helpers ──────────────────────────────────────────────────────────────

export const trackPageView = (path, title) => {
  if (!_initialized) return;
  ReactGA.send({ hitType: 'pageview', page: path, title });
};

export const trackEvent = (category, action, label, value) => {
  if (!_initialized) return;
  ReactGA.event({ category, action, ...(label !== undefined && { label }), ...(value !== undefined && { value }) });
};

// ── Page-level tracking ───────────────────────────────────────────────────────

export const trackPricingPageView = () =>
  trackPageView('/pricing', 'Pricing & Plans');

// ── CTA / Button click events ─────────────────────────────────────────────────

/** Landing page hero "Start writing — it's free" */
export const trackHeroCTAClick = () =>
  trackEvent('Engagement', 'cta_click', 'hero_start_writing');

/** Landing page "See pricing" scroll button */
export const trackSeePricingClick = () =>
  trackEvent('Engagement', 'cta_click', 'hero_see_pricing');

/** Landing page nav "Get started free" */
export const trackNavGetStartedClick = () =>
  trackEvent('Engagement', 'cta_click', 'nav_get_started');

/** Landing page nav "Sign in" */
export const trackNavSignInClick = () =>
  trackEvent('Engagement', 'cta_click', 'nav_sign_in');

/** Landing pricing section plan CTA */
export const trackLandingPlanClick = (planName) =>
  trackEvent('Engagement', 'cta_click', `landing_plan_${planName.toLowerCase()}`);

/** Pricing page: user clicks upgrade button for a specific plan */
export const trackUpgradeClick = (planName, planType) =>
  trackEvent('Conversion', 'upgrade_click', `${planType}_${planName.toLowerCase()}`);

/** Pricing page: billing period toggle */
export const trackBillingToggle = (period) =>
  trackEvent('Engagement', 'billing_toggle', period);

/** Pricing page FAQ expand */
export const trackFAQExpand = (question) =>
  trackEvent('Engagement', 'faq_expand', question.slice(0, 60));

// ── Auth / Conversion events ──────────────────────────────────────────────────

/** User lands on auth page */
export const trackAuthPageView = () =>
  trackPageView('/auth', 'Sign In');

/** Google OAuth button clicked */
export const trackGoogleSignInClick = () =>
  trackEvent('Auth', 'google_signin_click', 'auth_page');

/** Successful sign-in / signup (called after backend returns user) */
export const trackSignupSuccess = (isNewUser = false) =>
  trackEvent('Conversion', isNewUser ? 'signup' : 'login', 'google_oauth');

// ── Payment events ────────────────────────────────────────────────────────────

/** Razorpay widget opened */
export const trackPaymentInitiated = (planName, amount) =>
  trackEvent('Conversion', 'payment_initiated', planName, amount);

/** Payment verified successfully */
export const trackPaymentSuccess = (planName, amount) =>
  trackEvent('Conversion', 'payment_success', planName, amount);

// ── Engagement events ─────────────────────────────────────────────────────────

/** User spends significant time on landing page (call after 30 s) */
export const trackLandingEngagement = (seconds) =>
  trackEvent('Engagement', 'time_on_page', 'landing', seconds);
